import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import {
  LuArrowDown, LuArrowLeft, LuArrowRight, LuArrowUp, LuCheck, LuClipboard,
  LuEraser, LuExpand, LuKeyRound, LuLaptop, LuLoaderCircle, LuLockKeyhole,
  LuMinus, LuPlus, LuRefreshCw, LuScanLine, LuSettings2, LuShieldCheck,
  LuSquare, LuTerminal, LuUnplug,
} from 'react-icons/lu';
import { toast } from 'sonner';
import { api, getTerminalWebSocketUrl } from '../utils/api';
import { getTerminalErrorMessage, parseTerminalServerMessage, terminalUnavailablePresentation } from './terminalHelpers';
import 'xterm/css/xterm.css';

const EMPTY_FORM = { username: '', port: 22, auth_method: 'private_key', credential: '' };
const STATUS_STYLES = {
  connected: 'bg-emerald-400', connecting: 'bg-amber-400 animate-pulse',
  disconnected: 'bg-zinc-500', error: 'bg-red-400',
};

const TerminalWorkspace = ({ resourceId, resourceName }) => {
  const terminalElementRef = useRef(null);
  const workspaceRef = useRef(null);
  const xtermRef = useRef(null);
  const fitAddonRef = useRef(null);
  const socketRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const dataSubscriptionRef = useRef(null);
  const fitTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const connectionAttemptRef = useRef(0);
  const disconnectRequestedRef = useRef(false);
  const ctrlPendingRef = useRef(false);
  const connectionStateRef = useRef('disconnected');

  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configurationOpen, setConfigurationOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [scannedKey, setScannedKey] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [statusMessage, setStatusMessage] = useState('Ready when you are.');
  const [fontSize, setFontSize] = useState(14);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [ctrlPending, setCtrlPending] = useState(false);

  useEffect(() => { connectionStateRef.current = connectionState; }, [connectionState]);

  const showTerminal = access?.available && !configurationOpen;
  const target = access?.target || {};
  const usesJitCertificate = access?.authentication_mode === 'jit_certificate';
  const unavailablePresentation = terminalUnavailablePresentation(access);

  const updateFormFromAccess = useCallback((payload) => {
    const nextTarget = payload?.target || {};
    setForm({
      username: nextTarget.username || '', port: nextTarget.port || 22,
      auth_method: payload?.authentication_mode === 'jit_certificate' ? 'jit_certificate' : (nextTarget.auth_method || 'private_key'),
      credential: '',
    });
  }, []);

  const loadAccess = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getTerminalAccess(resourceId);
      setAccess(response.data);
      updateFormFromAccess(response.data);
      setConfigurationOpen(!response.data?.available && Boolean(response.data?.can_configure));
      setScannedKey(null);
    } catch (error) {
      setAccess({ available: false, can_configure: false, reason: getTerminalErrorMessage(error, 'Terminal readiness could not be checked.') });
    } finally {
      setLoading(false);
    }
  }, [resourceId, updateFormFromAccess]);

  useEffect(() => { loadAccess(); }, [loadAccess]);

  const sendSocketMessage = useCallback((payload) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  const fitAndNotify = useCallback(() => {
    const term = xtermRef.current;
    const fitAddon = fitAddonRef.current;
    if (!term || !fitAddon || !terminalElementRef.current) return;
    try {
      fitAddon.fit();
      if (term.cols > 0 && term.rows > 0) sendSocketMessage({ type: 'resize', cols: term.cols, rows: term.rows });
    } catch (error) {
      // The viewport may be between layouts while entering fullscreen.
    }
  }, [sendSocketMessage]);

  const sendInput = useCallback((data) => {
    if (connectionStateRef.current !== 'connected') return;
    let nextData = data;
    if (ctrlPendingRef.current && data.length === 1) {
      const code = data.toUpperCase().charCodeAt(0);
      if (code >= 64 && code <= 95) nextData = String.fromCharCode(code - 64);
      ctrlPendingRef.current = false;
      setCtrlPending(false);
    }
    sendSocketMessage({ type: 'input', data: nextData });
  }, [sendSocketMessage]);

  useEffect(() => {
    if (!showTerminal || !terminalElementRef.current) return undefined;
    const term = new XTerm({
      cursorBlink: true, cursorStyle: 'bar', convertEol: false,
      screenReaderMode: true,
      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
      fontSize, lineHeight: 1.25, scrollback: 5000,
      theme: {
        background: '#09090b', foreground: '#e4e4e7', cursor: '#2dd4bf',
        cursorAccent: '#09090b', selectionBackground: '#0f766e99', black: '#18181b',
        brightBlack: '#71717a', green: '#34d399', brightGreen: '#6ee7b7',
        cyan: '#2dd4bf', brightCyan: '#5eead4',
      },
    });
    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.open(terminalElementRef.current);
    xtermRef.current = term;
    fitAddonRef.current = fitAddon;
    dataSubscriptionRef.current = term.onData(sendInput);
    resizeObserverRef.current = new ResizeObserver(() => {
      window.clearTimeout(fitTimerRef.current);
      fitTimerRef.current = window.setTimeout(fitAndNotify, 60);
    });
    resizeObserverRef.current.observe(terminalElementRef.current);
    fitTimerRef.current = window.setTimeout(() => { fitAndNotify(); term.focus(); }, 80);
    return () => {
      window.clearTimeout(fitTimerRef.current);
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      dataSubscriptionRef.current?.dispose();
      dataSubscriptionRef.current = null;
      term.dispose();
      xtermRef.current = null;
      fitAddonRef.current = null;
    };
  }, [fitAndNotify, sendInput, showTerminal]);

  useEffect(() => {
    if (!xtermRef.current) return;
    xtermRef.current.options.fontSize = fontSize;
    fitAndNotify();
  }, [fontSize, fitAndNotify]);

  const closeSocket = useCallback((message = 'Session ended.') => {
    connectionAttemptRef.current += 1;
    disconnectRequestedRef.current = true;
    const socket = socketRef.current;
    socketRef.current = null;
    window.clearInterval(heartbeatRef.current);
    heartbeatRef.current = null;
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) socket.close(1000, 'Client disconnected');
    setConnectionState('disconnected');
    setStatusMessage(message);
  }, []);

  useEffect(() => () => closeSocket('Session ended.'), [closeSocket]);

  const startSession = async () => {
    closeSocket('Preparing a new secure session…');
    disconnectRequestedRef.current = false;
    const attempt = connectionAttemptRef.current;
    setConnectionState('connecting');
    setStatusMessage('Requesting short-lived terminal access…');
    try {
      const response = await api.createTerminalTicket(resourceId);
      if (attempt !== connectionAttemptRef.current || disconnectRequestedRef.current) return;
      const { ticket } = response.data;
      const socket = new WebSocket(getTerminalWebSocketUrl(resourceId), ['cloud-watcher']);
      socket.binaryType = 'arraybuffer';
      socketRef.current = socket;
      socket.onopen = () => {
        if (attempt !== connectionAttemptRef.current || disconnectRequestedRef.current) { socket.close(1000, 'Cancelled'); return; }
        socket.send(JSON.stringify({ type: 'auth', ticket }));
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify({ type: 'ping' }));
        }, 20000);
        setConnectionState('connecting');
        setStatusMessage('Verifying server identity and opening SSH…');
        xtermRef.current?.focus();
        fitAndNotify();
      };
      socket.onmessage = async (event) => {
        if (attempt !== connectionAttemptRef.current) return;
        if (typeof event.data !== 'string') {
          const bytes = event.data instanceof Blob ? new Uint8Array(await event.data.arrayBuffer()) : new Uint8Array(event.data);
          xtermRef.current?.write(bytes);
          return;
        }
        const message = parseTerminalServerMessage(event.data);
        if (message.type === 'output') xtermRef.current?.write(message.data);
        else if (message.type === 'error') {
          setConnectionState('error'); setStatusMessage(message.message);
          xtermRef.current?.writeln(`\r\n\x1b[31m${message.message}\x1b[0m`);
        } else if (message.type === 'status') {
          setStatusMessage(message.message || (message.state === 'connected' ? 'Connected with a short-lived certificate.' : 'Opening the secure SSH session…'));
          if (message.state === 'connected' || message.state === 'connecting') setConnectionState(message.state);
        }
      };
      socket.onerror = () => {
        if (attempt !== connectionAttemptRef.current) return;
        setConnectionState('error');
        setStatusMessage('The secure connection could not be established. Check the SSH settings and try again.');
      };
      socket.onclose = (event) => {
        if (attempt !== connectionAttemptRef.current) return;
        socketRef.current = null;
        window.clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
        if (disconnectRequestedRef.current) return;
        setConnectionState(event.code === 1000 ? 'disconnected' : 'error');
        setStatusMessage(event.reason || (event.code === 1000 ? 'Session ended.' : `Connection closed unexpectedly (${event.code}).`));
      };
    } catch (error) {
      if (attempt !== connectionAttemptRef.current) return;
      setConnectionState('error');
      setStatusMessage(getTerminalErrorMessage(error, 'Terminal authorization failed.'));
    }
  };

  const scanHostKey = async () => {
    setScanning(true); setScannedKey(null);
    try {
      const response = await api.scanTerminalHostKey(resourceId, { port: Number(form.port) });
      setScannedKey(response.data);
      toast.success('Host key found. Verify the fingerprint before saving.');
    } catch (error) {
      toast.error(getTerminalErrorMessage(error, 'The host key could not be scanned.'));
    } finally { setScanning(false); }
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    if (!scannedKey?.fingerprint) { toast.error('Scan and verify the host key before saving.'); return; }
    setSaving(true);
    try {
      await api.saveTerminalProfile(resourceId, {
        username: form.username.trim(), port: Number(form.port),
        auth_method: usesJitCertificate ? 'jit_certificate' : form.auth_method,
        credential: usesJitCertificate ? null : form.credential,
        expected_fingerprint: scannedKey.fingerprint,
      });
      toast.success('Secure terminal profile saved.');
      await loadAccess();
      setConfigurationOpen(false);
    } catch (error) {
      toast.error(getTerminalErrorMessage(error, 'Terminal settings could not be saved.'));
    } finally { setSaving(false); setForm((current) => ({ ...current, credential: '' })); }
  };

  const removeProfile = async () => {
    if (!window.confirm('Remove the saved terminal profile? This does not change the server itself.')) return;
    closeSocket();
    try { await api.deleteTerminalProfile(resourceId); toast.success('Terminal profile removed.'); await loadAccess(); }
    catch (error) { toast.error(getTerminalErrorMessage(error, 'Terminal profile could not be removed.')); }
  };

  const copySelection = async () => {
    const selection = xtermRef.current?.getSelection();
    if (!selection) { toast.info('Select text in the terminal first.'); return; }
    try { await navigator.clipboard.writeText(selection); toast.success('Selection copied.'); }
    catch (error) { toast.error('Clipboard access was not available.'); }
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await workspaceRef.current?.requestFullscreen();
    } catch (error) { toast.error('Fullscreen is not available in this browser.'); }
  };

  useEffect(() => {
    const handleFullscreen = () => { setIsFullscreen(document.fullscreenElement === workspaceRef.current); window.setTimeout(fitAndNotify, 50); };
    document.addEventListener('fullscreenchange', handleFullscreen);
    return () => document.removeEventListener('fullscreenchange', handleFullscreen);
  }, [fitAndNotify]);

  const mobileKeys = useMemo(() => [
    { label: 'Esc', data: '\x1b' }, { label: 'Tab', data: '\t' },
    { label: <LuArrowUp />, aria: 'Arrow up', data: '\x1b[A' },
    { label: <LuArrowDown />, aria: 'Arrow down', data: '\x1b[B' },
    { label: <LuArrowLeft />, aria: 'Arrow left', data: '\x1b[D' },
    { label: <LuArrowRight />, aria: 'Arrow right', data: '\x1b[C' },
  ], []);

  if (loading) return <div className="min-h-[420px] rounded-2xl border border-zinc-800 bg-zinc-900/50 flex items-center justify-center" role="status"><LuLoaderCircle className="h-5 w-5 animate-spin text-teal-400" /><span className="ml-3 text-sm text-zinc-300">Checking terminal readiness…</span></div>;

  if (!access?.available && !access?.can_configure) return (
    <div className="rounded-2xl border border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-950 p-8 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-zinc-800 text-zinc-400"><LuUnplug className="h-6 w-6" /></div>
      <h2 className="text-xl font-semibold text-zinc-100">{unavailablePresentation.title}</h2>
      <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-zinc-400">{unavailablePresentation.detail}</p>
      {unavailablePresentation.retryable && <button onClick={loadAccess} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-700"><LuRefreshCw /> Check status</button>}
    </div>
  );

  if (configurationOpen || !access?.available) return (
    <div className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/70 shadow-2xl shadow-black/20">
      <div className="border-b border-zinc-800 bg-gradient-to-r from-teal-500/10 via-zinc-900 to-zinc-900 px-6 py-5">
        <div className="flex items-start gap-4"><div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-teal-500/20 bg-teal-500/10 text-teal-300"><LuLockKeyhole className="h-5 w-5" /></div><div><p className="text-xs font-medium uppercase tracking-[0.18em] text-teal-400">Secure access setup</p><h2 className="mt-1 text-xl font-semibold text-zinc-100">Connect to {resourceName || 'this server'}</h2><p className="mt-1 text-sm text-zinc-400">{usesJitCertificate ? 'Cloud Watcher creates a temporary SSH key and certificate for each session. No private key or password is saved.' : 'Cloud Watcher stores this credential encrypted. A session never starts until you choose Start secure session.'}</p></div></div>
      </div>
      <form onSubmit={saveProfile} className="grid gap-6 p-6 lg:grid-cols-[1fr_0.9fr]">
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-[1fr_130px]">
            <label className="block text-sm text-zinc-300">Username<input required autoComplete="username" value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} placeholder="ubuntu" className="mt-2 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-zinc-100 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20" /></label>
            <label className="block text-sm text-zinc-300">SSH port<input required min="1" max="65535" type="number" value={form.port} onChange={(event) => { setForm({ ...form, port: event.target.value }); setScannedKey(null); }} className="mt-2 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-zinc-100 outline-none focus:border-teal-500" /></label>
          </div>
          {usesJitCertificate ? <div className="rounded-lg border border-teal-500/20 bg-teal-500/5 p-4"><div className="flex items-center gap-2 text-sm font-medium text-teal-300"><LuKeyRound /> Temporary certificate</div><p className="mt-2 text-xs leading-5 text-zinc-400">Each connection receives its own 15-minute SSH certificate. Your active terminal grant is checked again while the session is open.</p></div> : <><label className="block text-sm text-zinc-300">Authentication<select value={form.auth_method} onChange={(event) => setForm({ ...form, auth_method: event.target.value, credential: '' })} className="mt-2 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-zinc-100 outline-none focus:border-teal-500"><option value="private_key">Private SSH key</option><option value="password">Password</option></select></label><label className="block text-sm text-zinc-300">{form.auth_method === 'private_key' ? 'Private key' : 'Password'}{access?.available && <span className="ml-2 text-xs font-normal text-zinc-500">Leave blank to keep the saved credential</span>}{form.auth_method === 'private_key' ? <textarea required={!access?.available} rows="7" autoComplete="off" spellCheck="false" value={form.credential} onChange={(event) => setForm({ ...form, credential: event.target.value })} placeholder={access?.available ? 'Paste only to replace the saved private key' : 'Paste the private key for this server'} className="mt-2 w-full resize-y rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 font-mono text-xs leading-5 text-zinc-100 outline-none focus:border-teal-500" /> : <input required={!access?.available} type="password" autoComplete="new-password" value={form.credential} onChange={(event) => setForm({ ...form, credential: event.target.value })} placeholder={access?.available ? 'Enter only to replace the saved password' : 'Enter the SSH password'} className="mt-2 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2.5 text-zinc-100 outline-none focus:border-teal-500" />}</label></>}
        </div>
        <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-5">
          <div className="flex items-center gap-2 text-sm font-medium text-zinc-200"><LuShieldCheck className="text-teal-400" /> Verify server identity</div>
          <p className="mt-2 text-xs leading-5 text-zinc-500">Scan the SSH host key, compare the fingerprint with your provider or server console, then explicitly trust it.</p>
          <button type="button" onClick={scanHostKey} disabled={scanning || !form.port} className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2.5 text-sm text-zinc-200 hover:bg-zinc-700 disabled:opacity-50">{scanning ? <LuLoaderCircle className="animate-spin" /> : <LuScanLine />} {scanning ? 'Scanning host key…' : 'Scan host key'}</button>
          {scannedKey && <div className="mt-4 rounded-lg border border-teal-500/20 bg-teal-500/5 p-4" role="status"><div className="flex items-center gap-2 text-sm font-medium text-teal-300"><LuCheck /> Host key received</div><dl className="mt-3 space-y-2 text-xs"><div className="flex justify-between gap-4"><dt className="text-zinc-500">Target</dt><dd className="text-right font-mono text-zinc-300">{scannedKey.host_masked}:{scannedKey.port}</dd></div><div className="flex justify-between gap-4"><dt className="text-zinc-500">Key type</dt><dd className="font-mono text-zinc-300">{scannedKey.key_type}</dd></div></dl><div className="mt-3 border-t border-zinc-800 pt-3"><p className="text-[11px] uppercase tracking-wider text-zinc-500">Fingerprint</p><p className="mt-1 break-all font-mono text-xs leading-5 text-zinc-200">{scannedKey.fingerprint}</p></div></div>}
          <p className="mt-4 flex items-start gap-2 text-xs leading-5 text-zinc-500"><LuKeyRound className="mt-0.5 shrink-0" /> Saving confirms that you trust this exact host key. A changed key will block future connections.</p>
        </div>
        <div className="flex flex-col-reverse gap-3 border-t border-zinc-800 pt-5 sm:flex-row sm:items-center sm:justify-between lg:col-span-2">
          <div className="flex gap-2">{access?.available && <button type="button" onClick={() => { setConfigurationOpen(false); setScannedKey(null); }} className="rounded-lg px-4 py-2.5 text-sm text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200">Cancel</button>}{access?.available && <button type="button" onClick={removeProfile} className="rounded-lg px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10">Remove profile</button>}</div>
          <button type="submit" disabled={saving || !scannedKey?.fingerprint} className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-500 px-5 py-2.5 text-sm font-semibold text-zinc-950 hover:bg-teal-400 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400">{saving ? <LuLoaderCircle className="animate-spin" /> : <LuShieldCheck />} {saving ? 'Saving securely…' : 'Trust fingerprint & save'}</button>
        </div>
      </form>
    </div>
  );

  return (
    <div ref={workspaceRef} className={`overflow-hidden border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/30 ${isFullscreen ? 'h-screen rounded-none' : 'rounded-2xl'}`}>
      <div className="flex flex-col gap-4 border-b border-zinc-800 bg-zinc-900/90 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3"><div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-zinc-700 bg-zinc-800 text-teal-300"><LuTerminal /></div><div className="min-w-0"><div className="flex items-center gap-2"><h2 className="truncate text-sm font-semibold text-zinc-100">{resourceName || 'Secure terminal'}</h2><span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_STYLES[connectionState]}`} /></div><p className="mt-0.5 truncate font-mono text-xs text-zinc-500">{target.username}@{target.host_masked}:{target.port} · {usesJitCertificate ? 'Temporary SSH certificate' : target.auth_method === 'private_key' ? 'SSH key' : 'Password'}</p></div></div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => { closeSocket('Session ended.'); setConfigurationOpen(true); }} disabled={connectionState === 'connecting' || connectionState === 'connected'} title="Connection settings" aria-label="Connection settings" className="rounded-lg border border-zinc-700 bg-zinc-800 p-2 text-zinc-400 hover:text-white disabled:opacity-40"><LuSettings2 /></button>
          <button onClick={copySelection} title="Copy selection" aria-label="Copy selection" className="rounded-lg border border-zinc-700 bg-zinc-800 p-2 text-zinc-400 hover:text-white"><LuClipboard /></button>
          <button onClick={() => xtermRef.current?.clear()} title="Clear terminal" aria-label="Clear terminal" className="rounded-lg border border-zinc-700 bg-zinc-800 p-2 text-zinc-400 hover:text-white"><LuEraser /></button>
          <div className="flex items-center rounded-lg border border-zinc-700 bg-zinc-800"><button onClick={() => setFontSize((size) => Math.max(11, size - 1))} aria-label="Decrease font size" className="p-2 text-zinc-400 hover:text-white"><LuMinus /></button><span className="min-w-8 text-center text-[11px] text-zinc-500">{fontSize}</span><button onClick={() => setFontSize((size) => Math.min(20, size + 1))} aria-label="Increase font size" className="p-2 text-zinc-400 hover:text-white"><LuPlus /></button></div>
          <button onClick={toggleFullscreen} title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} className="rounded-lg border border-zinc-700 bg-zinc-800 p-2 text-zinc-400 hover:text-white"><LuExpand /></button>
          {connectionState === 'connected' || connectionState === 'connecting' ? <button onClick={() => closeSocket('Session disconnected.')} className="inline-flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-300 hover:bg-red-500/20"><LuSquare /> Disconnect</button> : <button onClick={startSession} className="inline-flex items-center gap-2 rounded-lg bg-teal-500 px-3 py-2 text-xs font-semibold text-zinc-950 hover:bg-teal-400"><LuLaptop /> {connectionState === 'error' ? 'Reconnect' : 'Start secure session'}</button>}
        </div>
      </div>
      <div className={`flex items-center gap-2 border-b px-4 py-2 text-xs ${connectionState === 'error' ? 'border-red-500/20 bg-red-500/10 text-red-300' : 'border-zinc-800 bg-zinc-900/50 text-zinc-400'}`} role="status" aria-live="polite" aria-atomic="true">{connectionState === 'connecting' ? <LuLoaderCircle className="animate-spin" /> : connectionState === 'connected' ? <LuShieldCheck className="text-emerald-400" /> : <LuLockKeyhole />}<span>{statusMessage}</span></div>
      <div className={`relative bg-[#09090b] ${isFullscreen ? 'h-[calc(100vh-146px)]' : 'h-[520px] min-h-[360px]'}`}>
        {connectionState === 'disconnected' && <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-zinc-950/80 px-6 text-center backdrop-blur-[2px]"><div><LuTerminal className="mx-auto h-8 w-8 text-zinc-600" /><p className="mt-3 text-sm font-medium text-zinc-300">No session is running</p><p className="mt-1 text-xs text-zinc-500">Start a secure session when you are ready. Nothing connects automatically.</p></div></div>}
        <div ref={terminalElementRef} className="h-full w-full p-3" aria-label={`Terminal for ${resourceName || 'server'}`} />
      </div>
      <div className="flex items-center gap-2 overflow-x-auto border-t border-zinc-800 bg-zinc-900 px-3 py-2 sm:hidden" aria-label="Mobile terminal keys">
        <button onClick={() => { const next = !ctrlPending; ctrlPendingRef.current = next; setCtrlPending(next); xtermRef.current?.focus(); }} aria-pressed={ctrlPending} className={`shrink-0 rounded-md border px-3 py-2 text-xs ${ctrlPending ? 'border-teal-500 bg-teal-500/20 text-teal-300' : 'border-zinc-700 bg-zinc-800 text-zinc-300'}`}>Ctrl</button>
        {mobileKeys.map((key) => <button key={key.aria || key.label} onClick={() => { sendInput(key.data); xtermRef.current?.focus(); }} aria-label={key.aria || key.label} className="flex min-h-9 min-w-11 shrink-0 items-center justify-center rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-xs text-zinc-300 active:bg-zinc-700">{key.label}</button>)}
      </div>
    </div>
  );
};

export default TerminalWorkspace;
